import {
  programmeQuery,
  programmeTasksQuery,
} from "@common/api/maintenance-programme"
import type {
  ProgrammeDetailPublic,
  ProgrammeRevisionDetailPublic,
} from "@common/client"
import { DropdownMenuItem } from "@common/components/ui/dropdown-menu"
import { useEscape } from "@common/hooks/use-escape"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ListChecks, Trash2 } from "lucide-react"
import {
  DetailPane,
  PaneMenu,
  PaneNumber,
} from "@/components/layout/detail-panel"
import { ListEmpty } from "@/components/layout/list-empty"
import { ExpandToggle, PaneStepper } from "@/components/page/pane"
import useCustomToast from "@/hooks/use-custom-toast"
import { revisionLabel } from "@/routes/_layout/maintenance-programmes/-lib/revision-label"
import { RevisionBadge } from "../-components/pane-heading"
import { TaskForm } from "../-components/task-form/task-form"
import { editBlocker, useViewedRevision } from "../-lib/revision"
import { TASKS } from "../-lib/search"
import { useTaskOrder } from "../-lib/task-order"

export const Route = createFileRoute(
  "/_layout/maintenance-programmes_/$programmeId/tasks/$taskId",
)({
  component: TaskPane,
})

function TaskPane(): React.ReactElement {
  const { programmeId, taskId } = Route.useParams()
  const { data: programme } = useSuspenseQuery(
    programmeQuery({ programme_id: programmeId }),
  )
  const revision = useViewedRevision(programme)
  const navigate = useNavigate()
  const close = () =>
    navigate({
      to: "/maintenance-programmes/$programmeId",
      params: { programmeId },
      search: true,
    })
  useEscape(close)

  return revision ? (
    <RevisionTaskPane
      programme={programme}
      taskId={taskId}
      revision={revision}
      onClose={close}
    />
  ) : (
    <MissingTask onClose={close} />
  )
}

function RevisionTaskPane({
  programme,
  taskId,
  revision,
  onClose,
}: {
  programme: ProgrammeDetailPublic
  taskId: string
  revision: ProgrammeRevisionDetailPublic
  onClose: () => void
}): React.ReactElement {
  const scope = { programme_id: programme.id, revision_id: revision.id }
  const { data: tasks } = useSuspenseQuery(
    programmeTasksQuery({ ...scope, ...TASKS }),
  )
  const order = useTaskOrder(scope)
  const canUpdate = useHasPermission("maintenance_programme:update")
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const task = tasks.data.find(({ id }) => id === taskId)
  const blocked = editBlocker(programme, revision, canUpdate)
  const index = order.indexOf(taskId)
  const params = { programmeId: programme.id }

  if (!task) return <MissingTask onClose={onClose} />

  const step = (offset: number) => {
    const target = index === -1 ? undefined : order[index + offset]
    if (target) {
      void navigate({
        to: "/maintenance-programmes/$programmeId/tasks/$taskId",
        params: { ...params, taskId: target },
        search: true,
      })
    }
  }

  return (
    <DetailPane
      title={task.task_number ?? task.title}
      heading={
        <>
          <PaneNumber>{task.task_number ?? task.title}</PaneNumber>
          <RevisionBadge revision={revision} />
        </>
      }
      actions={
        <TaskPaneActions
          index={index}
          total={order.length}
          onStep={step}
          onRemove={
            blocked === undefined
              ? () =>
                  navigate({
                    to: "/maintenance-programmes/$programmeId/tasks/remove/$taskId",
                    params: { ...params, taskId },
                    search: true,
                  })
              : undefined
          }
        />
      }
      onClose={onClose}
    >
      <TaskForm
        key={task.id}
        scope={scope}
        task={task}
        blocked={blocked}
        onSaved={(saved) =>
          showToast(
            `Saved ${saved.task_number ?? saved.title}`,
            revisionLabel(revision.number),
            "success",
          )
        }
        onCancel={onClose}
      />
    </DetailPane>
  )
}

function TaskPaneActions({
  index,
  total,
  onStep,
  onRemove,
}: {
  index: number
  total: number
  onStep: (offset: number) => void
  onRemove: (() => void) | undefined
}): React.ReactElement {
  return (
    <>
      <PaneStepper
        noun="task"
        index={index}
        total={total}
        onPrevious={index > 0 ? () => onStep(-1) : null}
        onNext={index !== -1 && index < total - 1 ? () => onStep(1) : null}
      />
      <PaneMenu label="Task actions">
        {onRemove && (
          <DropdownMenuItem onSelect={onRemove}>
            <Trash2 />
            Remove from revision
          </DropdownMenuItem>
        )}
      </PaneMenu>
      <ExpandToggle />
    </>
  )
}

function MissingTask({ onClose }: { onClose: () => void }): React.ReactElement {
  return (
    <DetailPane title="Task" onClose={onClose}>
      <ListEmpty
        icon={ListChecks}
        title="Task not in this revision"
        body="It may have been removed, or it belongs to another revision."
      />
    </DetailPane>
  )
}
